import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const STEPS = [
  { key: 'welcome', placement: 'center' },
  { key: 'online', target: '[data-tour="online"]', placement: 'bottom' },
  { key: 'accept', target: '[data-tour="tips"]', placement: 'top' },
  { key: 'deliver', target: '[data-tour="history"]', placement: 'top' },
];

const PAD = 10;

function useTargetRect(selector, active) {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(selector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - PAD,
      left: r.left - PAD,
      width: r.width + PAD * 2,
      height: r.height + PAD * 2,
    });
  }, [selector]);

  useLayoutEffect(() => {
    if (!active) {
      setRect(null);
      return undefined;
    }
    if (!selector) return undefined;

    const el = document.querySelector(selector);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });

    measure();
    const retry = setTimeout(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearTimeout(retry);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, selector, measure]);

  return rect;
}

function tooltipPosition(rect, placement) {
  const margin = 12;
  const maxW = Math.min(340, window.innerWidth - 32);
  if (!rect || placement === 'center') {
    return { style: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: maxW }, arrow: null };
  }
  const left = Math.min(Math.max(16, rect.left + rect.width / 2 - maxW / 2), window.innerWidth - maxW - 16);
  if (placement === 'bottom') {
    return {
      style: { top: rect.top + rect.height + margin, left, width: maxW },
      arrow: { top: rect.top + rect.height + 4, left: rect.left + rect.width / 2 },
    };
  }
  const tooltipH = 200;
  return {
    style: { top: Math.max(16, rect.top - margin - tooltipH), left, width: maxW },
    arrow: { top: rect.top - 8, left: rect.left + rect.width / 2 },
  };
}

export default function FirstTimeTour({ open, onFinish }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(0);

  const current = STEPS[step];
  const rect = useTargetRect(current.target, open && !!current.target);
  const { style: tooltipStyle, arrow } = tooltipPosition(rect, current.placement);
  const isLast = step === STEPS.length - 1;
  const isCenter = current.placement === 'center';

  useEffect(() => {
    if (!open) setStep(0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const stepKey = current.key;

  return (
    <div className="tour-layer" role="presentation">
      {!isCenter && rect && (
        <>
          <div
            className="tour-spotlight"
            style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
            aria-hidden
          />
          {arrow && <div className="tour-arrow" style={{ top: arrow.top, left: arrow.left }} aria-hidden />}
        </>
      )}
      {isCenter && <div className="tour-dim" aria-hidden />}

      <div
        className={`tour-tooltip${isCenter ? ' tour-tooltip--center' : ''}`}
        style={tooltipStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rider-tour-title"
      >
        <div className="tour-tooltip__icon" aria-hidden>{t(`tour.${stepKey}Icon`)}</div>
        <h2 id="rider-tour-title" className="tour-tooltip__title">{t(`tour.${stepKey}Title`)}</h2>
        <p className="tour-tooltip__body">{t(`tour.${stepKey}Body`)}</p>

        <div className="tour-dots" aria-hidden>
          {STEPS.map((s, i) => (
            <span key={s.key} className={`tour-dot${i === step ? ' tour-dot--active' : ''}`} />
          ))}
        </div>

        <div className="tour-tooltip__actions">
          <button type="button" className="btn btn-dark btn-sm" onClick={onFinish}>
            {t('tour.skip')}
          </button>
          {isLast ? (
            <button type="button" className="btn btn-green btn-sm" onClick={onFinish}>
              {t('tour.gotIt')}
            </button>
          ) : (
            <button type="button" className="btn btn-green btn-sm" onClick={() => setStep((s) => s + 1)}>
              {t('tour.next')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
